import { z } from 'zod'
import { ADULT_TEETH, CHILD_TEETH } from './constants'

const adultToothEnum = z.union(ADULT_TEETH.map((t) => z.literal(t)))
const childToothEnum = z.union(CHILD_TEETH.map((t) => z.literal(t)))

const affectedTeethSchema = z.object({
  adult_teeth: z.array(adultToothEnum).default([]),
  child_teeth: z.array(childToothEnum).default([]),
})

/* ----------------------------- Phase 1 ------------------------------- */
export const patientSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(200),
  dob: z
    .string()
    .min(1, 'Date of birth is required')
    .refine((v) => new Date(v) <= new Date(), {
      message: 'DOB cannot be in the future',
    }),
  gender: z.enum(['male', 'female', 'other'], {
    errorMap: () => ({ message: 'Please select a gender' }),
  }),
  mobile_number: z
    .string()
    .trim()
    .min(5, 'Mobile number is required')
    .max(20)
    .regex(/^[0-9+\-\s]+$/, 'Only digits, +, -, spaces allowed'),
  whatsapp_number: z
    .string()
    .trim()
    .max(20)
    .regex(/^[0-9+\-\s]*$/, 'Only digits, +, -, spaces allowed')
    .optional()
    .or(z.literal('')),
  address: z.string().trim().optional().or(z.literal('')),
  occupation: z.string().trim().optional().or(z.literal('')),
  referred_by: z.string().trim().optional().or(z.literal('')),
  registration_date: z.string().optional().or(z.literal('')),
})

export const patientUpdateSchema = patientSchema.partial()

/* ----------------------------- Phase 2 ------------------------------- */
export const vitalsSchema = z.object({
  blood_pressure: z.string().trim().optional().or(z.literal('')),
  pulse_rate: z.string().trim().optional().or(z.literal('')),
  respiratory_rate: z.string().trim().optional().or(z.literal('')),
  temperature: z.string().trim().optional().or(z.literal('')),
  spo2: z.string().trim().optional().or(z.literal('')),
  random_blood_sugar: z.string().trim().optional().or(z.literal('')),
})

export const historyPresentIllnessSchema = z.object({
  duration: z.string().trim().optional().or(z.literal('')),
  type_of_pain: z
    .array(z.enum(['sharp', 'dull', 'throbbing']))
    .default([]),
  aggravating_factors: z.string().trim().optional().or(z.literal('')),
  relieving_factors: z.string().trim().optional().or(z.literal('')),
  previous_dental_treatment: z.string().trim().optional().or(z.literal('')),
  swelling_fever_pus: z
    .array(z.enum(['swelling', 'fever', 'pus_discharge']))
    .default([]),
})

export const medicalHistorySchema = z.object({
  conditions: z
    .array(
      z.enum([
        'diabetes',
        'hypertension',
        'thyroid_disorder',
        'asthma',
        'heart_disease',
        'bleeding_disorders',
        'pregnancy_lactation',
      ]),
    )
    .default([]),
  allergies: z.string().trim().optional().or(z.literal('')),
  current_medications: z.string().trim().optional().or(z.literal('')),
  previous_surgeries: z.string().trim().optional().or(z.literal('')),
})

export const personalHistorySchema = z.object({
  habits: z
    .array(z.enum(['smoking', 'tobacco_chewing', 'alcohol']))
    .default([]),
  diet: z.enum(['veg', 'non_veg', 'mixed']).optional().nullable(),
  brushing_frequency: z.string().trim().optional().or(z.literal('')),
  mouthwash_floss: z.array(z.enum(['mouthwash', 'floss'])).default([]),
})

/* ----------------------------- Phase 3 ------------------------------- */
export const chiefComplaintSchema = z.object({
  complaint_text: z.string().trim().min(1, 'Please describe the chief complaint'),
})

export const dentalHistorySchema = z.object({
  previous_extraction: z.string().trim().optional().or(z.literal('')),
  root_canal_treatment: z.string().trim().optional().or(z.literal('')),
  orthodontic_treatment: z.string().trim().optional().or(z.literal('')),
  denture_prosthesis: z.string().trim().optional().or(z.literal('')),
  last_dental_visit: z.string().trim().optional().or(z.literal('')),
})

export const extraOralSchema = z.object({
  facial_symmetry: z.string().trim().optional().or(z.literal('')),
  swelling: z.string().trim().optional().or(z.literal('')),
  lymph_nodes: z.string().trim().optional().or(z.literal('')),
  tmj_findings: z.string().trim().optional().or(z.literal('')),
  mouth_opening: z.string().trim().optional().or(z.literal('')),
})

// New structured intra-oral schema
const dentalCariesSchema = z.object({
  present: z.boolean().default(false),
  grade_1: affectedTeethSchema.optional().nullable(),
  grade_2: affectedTeethSchema.optional().nullable(),
  grade_3: affectedTeethSchema.optional().nullable(),
  grade_4: affectedTeethSchema.optional().nullable(),
  grade_5: affectedTeethSchema.optional().nullable(),
  grade_6: affectedTeethSchema.optional().nullable(),
})

const toothMobilitySchema = z.object({
  present: z.boolean().default(false),
  grade_1: affectedTeethSchema.optional().nullable(),
  grade_2: affectedTeethSchema.optional().nullable(),
  grade_3: affectedTeethSchema.optional().nullable(),
})

export const intraOralSchema = z.object({
  oral_hygiene_status: z.enum(['poor', 'medium', 'good']).optional().nullable(),
  gingiva: z.enum(['bleeding', 'not_bleeding']).optional().nullable(),
  recession: z.enum(['grade_1', 'grade_2', 'grade_3']).optional().nullable(),
  calculus_stains: z.enum(['1', '2', '3']).optional().nullable(),
  caries: dentalCariesSchema.optional().nullable(),
  tooth_mobility: toothMobilitySchema.optional().nullable(),
  missing_teeth: affectedTeethSchema.optional().nullable(),
  soft_tissue_findings: z.string().trim().optional().or(z.literal('')),
  occlusion: z.string().trim().optional().or(z.literal('')),
})

/* ----------------------------- Phase 4 ------------------------------- */
export const investigationSchema = z.object({
  investigations: z
    .array(
      z.enum(['iopa', 'opg', 'cbct', 'pulp_vitality_test', 'blood_investigations']),
    )
    .default([]),
  notes: z.string().trim().optional().or(z.literal('')),
})

export const provisionalDiagnosisSchema = z.object({
  diagnosis_text: z.string().trim().min(1, 'Diagnosis is required'),
})

// Updated treatment plan: multi-select + optional notes
export const treatmentPlanSchema = z
  .object({
    treatments: z
      .array(
        z.enum([
          'rct',
          'filling',
          'extraction',
          'implant',
          'rpd',
          'cd',
          'scaling',
          'bleaching',
          'veneers',
          'splint',
          'fluoride_therapy',
        ]),
      )
      .default([]),
    plan_text: z.string().trim().optional().or(z.literal('')),
  })
  .refine(
    (v) => (v.treatments && v.treatments.length > 0) || (v.plan_text && v.plan_text.length > 0),
    {
      message: 'Pick at least one treatment or add a note',
      path: ['treatments'],
    },
  )

/* ----------------------------- Phase 5 ------------------------------- */
export const medicineItemSchema = z.object({
  medicine: z.string().trim().min(1, 'Medicine name'),
  dosage: z.string().trim().min(1, 'Dosage'),
  duration: z.string().trim().min(1, 'Duration'),
})

export const prescriptionSchema = z.object({
  medicines: z.array(medicineItemSchema).default([]),
})

export const consentSchema = z.object({
  written_consent_obtained: z.boolean().default(false),
  procedure_explained: z.boolean().default(false),
  risks_explained: z.boolean().default(false),
})

export const doctorNotesSchema = z.object({
  special_precautions: z.string().trim().optional().or(z.literal('')),
  follow_up_advice: z.string().trim().optional().or(z.literal('')),
  emergency_instructions: z.string().trim().optional().or(z.literal('')),
})

/* ----------------------------- Phase 6 — Drug list ---------------------- */
const DRUG_CATEGORY_ENUM = z.enum([
  'tablet',
  'gel',
  'paste',
  'mouthwash',
  'brush_adult',
  'brush_kids',
  'ortho',
  'antibiotics',
  'pain_killers',
])

export const drugCreateSchema = z
  .object({
    name: z.string().trim().min(1, 'Medicine name is required').max(200),
    category: DRUG_CATEGORY_ENUM,
    dosage: z.string().trim().max(100).optional().or(z.literal('')),
    stock_count: z.coerce.number().int('Whole number').min(0, 'Cannot be negative'),
    reorder_level: z
      .union([z.coerce.number().int().min(0), z.literal(''), z.null()])
      .optional()
      .transform((v) => (v === '' || v === null ? undefined : v)),
    mfg_date: z.string().optional().or(z.literal('')),
    exp_date: z.string().min(1, 'Expiry date is required'),
    mrp: z.coerce.number().min(0, 'MRP must be 0 or more'),
  })
  .refine(
    (v) => {
      if (!v.mfg_date || !v.exp_date) return true
      return new Date(v.mfg_date) <= new Date(v.exp_date)
    },
    { message: 'Mfg date cannot be after expiry date', path: ['mfg_date'] },
  )

export const drugUpdateSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  category: DRUG_CATEGORY_ENUM.optional(),
  dosage: z.string().trim().max(100).optional().or(z.literal('')),
  reorder_level: z
    .union([z.coerce.number().int().min(0), z.literal(''), z.null()])
    .optional()
    .transform((v) => (v === '' || v === null ? undefined : v)),
  mfg_date: z.string().optional().or(z.literal('')),
  exp_date: z.string().optional().or(z.literal('')),
  mrp: z.coerce.number().min(0).optional(),
  is_active: z.boolean().optional(),
})

export const stockAdjustSchema = z
  .object({
    delta: z.coerce.number().int('Whole number').refine((n) => n !== 0, {
      message: 'Delta cannot be zero',
    }),
    note: z.string().trim().max(300).optional().or(z.literal('')),
  })

/* ----------------------------- Phase 7 — Billing ------------------------- */
export const billItemSchema = z.object({
  drug_id: z.string().trim().min(1, 'Pick a medicine'),
  quantity: z.coerce.number().int().min(1, 'Quantity must be at least 1'),
})

export const billCreateSchema = z
  .object({
    patient_id: z.string().trim().min(1, 'Pick a patient'),
    visit_id: z.string().trim().optional().or(z.literal('')),
    treatment_plan: z.string().trim().optional().or(z.literal('')),
    service_charge: z.coerce.number().min(0).default(0),
    paid: z.coerce.number().min(0).default(0),
    bill_date: z.string().optional().or(z.literal('')),
    notes: z.string().trim().optional().or(z.literal('')),
    items: z.array(billItemSchema).default([]),
  })
  .refine(
    (v) => v.service_charge > 0 || (v.items && v.items.length > 0),
    {
      message: 'Add a service charge or at least one medicine',
      path: ['service_charge'],
    },
  )

export const billPaymentUpdateSchema = z.object({
  paid: z.coerce.number().min(0, 'Paid amount cannot be negative'),
})

/* ----------------------------- App settings ----------------------------- */
export const settingUpdateSchema = z.object({
  value: z.string().trim().min(1, 'Value is required'),
})
